// This doesn't use @atlassian/jira-navigation-system as it results in a hard-to-debug cycle.
// We need to move away from using these variables directly as it results in a brittle hard to evolve system.
// eslint-disable-next-line jira/restricted/@atlaskit+page-layout
import {
	LEFT_PANEL_WIDTH as UNSAFE_DO_NOT_USE_LEFT_PANEL_WIDTH,
	LEFT_SIDEBAR_WIDTH as UNSAFE_DO_NOT_USE_LEFT_SIDEBAR_WIDTH,
	RIGHT_PANEL_WIDTH as UNSAFE_DO_NOT_USE_RIGHT_PANEL_WIDTH,
	RIGHT_SIDEBAR_WIDTH as UNSAFE_DO_NOT_USE_RIGHT_SIDEBAR_WIDTH,
} from '@atlaskit/page-layout';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { PAGE_LAYOUT_OFFSET_TOP } from '@atlassian/jira-common-constants/src/page-layout.tsx';
import type {
	ProjectThemeTokens,
	PartialProjectThemeTokens,
	ImageBackgroundConfig,
	ColorBackgroundConfig,
	GradientBackgroundConfig,
	OldThemeName,
	Token,
	OldBackgroundConfig,
	AdaptiveGradientBackgroundConfig,
	AdaptiveColorBackgroundConfig,
	AdaptiveColorThemeName,
	AdaptiveGradientThemeName,
	BaseThemeTokens,
} from './types.tsx';

export const STAR_ICON_COLOR = '#FFAB00';

export const Variables: Record<Token, string> = {
	BACKDROP_FILTER: '--project-backdrop-filter', // Delete when business projects are migrated to the new theming
	COLOR_BACKGROUND_NEUTRAL_SUBTLE_HOVERED_CONTRAST:
		'--project-color-background-neutral-subtle-hovered-contrast',
	COLOR_BACKGROUND_ACCENT_SELECTED: '--project-color-background-accent-selected',
	COLOR_BACKGROUND_BRAND_BOLD: '--project-color-background-brand-bold',
	COLOR_BACKGROUND_BRAND_BOLD_HOVERED: '--project-color-background-brand-bold-hovered',
	COLOR_BACKGROUND_BRAND_BOLD_PRESSED: '--project-color-background-brand-bold-pressed',
	COLOR_BACKGROUND_DISABLED: '--project-color-background-disabled',
	COLOR_BACKGROUND_INPUT: '--project-background-input',
	COLOR_BACKGROUND_NEUTRAL: '--project-background-neutral',
	COLOR_BACKGROUND_NEUTRAL_HOVERED: '--project-background-neutral-hovered',
	COLOR_BACKGROUND_NEUTRAL_PRESSED: '--project-background-neutral-pressed',
	COLOR_BACKGROUND_NEUTRAL_SUBTLE_HOVERED: '--project-background-neutral-subtle-hovered',
	COLOR_BACKGROUND_NEUTRAL_SUBTLE_PRESSED: '--project-background-neutral-subtle-pressed',
	COLOR_BACKGROUND_SELECTED: '--project-background-selected',
	COLOR_BORDER: '--project-color-border',
	COLOR_BORDER_INPUT: '--project-color-border-input',
	COLOR_BORDER_INVERSE: '--project-color-border-inverse',
	COLOR_BORDER_SELECTED: '--project-color-border-selected',
	COLOR_ELEVATION_SURFACE: '--project-color-elevation-surface',
	COLOR_ELEVATION_SURFACE_SUNKEN: '--project-color-elevation-surface-sunken',
	COLOR_ICON_DANGER: '--project-color-icon-danger',
	COLOR_ICON_INFORMATION: '--project-color-icon-information',
	COLOR_ICON_SUCCESS: '--project-color-icon-success',
	COLOR_ICON_WARNING: '--project-color-icon-warning',
	COLOR_LINK: '--project-color-link',
	COLOR_LINK_PRESSED: '--project-color-link-pressed',
	COLOR_SKELETON: '--project-skeleton',
	COLOR_TEXT: '--project-color-text',
	COLOR_TEXT_ACCENT_SELECTED: '--project-text-accent-selected',
	COLOR_TEXT_DISABLED: '--project-color-text-disabled',
	COLOR_TEXT_INVERSE: '--project-color-text-inverse',
	COLOR_TEXT_SELECTED: '--project-color-text-selected',
	COLOR_TEXT_SUBTLE: '--project-color-text-subtle',
	COLOR_TEXT_SUBTLEST: '--project-color-text-subtlest',
	CONTENT_HEIGHT: '--project-content-height',
	CONTENT_WIDTH: '--project-content-width',
	ELEVATION_SURFACE: '--project-elevation-surface',
	ELEVATION_SURFACE_CONTRAST: '--project-elevation-surface-contrast',
	ELEVATION_SURFACE_CONTRAST_BOLDER: '--project-elevation-surface-contrast-bolder',
	ELEVATION_SURFACE_BLEND_MODE: '--project-elevation-surface-blend-mode',
	WRAPPER_BACKGROUND: '--project-wrapper-background',
	WRAPPER_BORDER_RADIUS: '--project-wrapper-border-radius',
	WRAPPER_HEIGHT: '--project-wrapper-height',
	WRAPPER_PADDING: '--project-wrapper-padding',
	WRAPPER_WIDTH: '--project-wrapper-width',
};

export const Tokens: Record<Token, string> = {
	BACKDROP_FILTER: 'var(--project-backdrop-filter)',
	COLOR_BACKGROUND_NEUTRAL_SUBTLE_HOVERED_CONTRAST:
		'var(--project-color-background-neutral-subtle-hovered-contrast)',
	COLOR_BACKGROUND_ACCENT_SELECTED: 'var(--project-color-background-accent-selected)',
	COLOR_BACKGROUND_BRAND_BOLD: 'var(--project-color-background-brand-bold)',
	COLOR_BACKGROUND_BRAND_BOLD_HOVERED: 'var(--project-color-background-brand-bold-hovered)',
	COLOR_BACKGROUND_BRAND_BOLD_PRESSED: 'var(--project-color-background-brand-bold-pressed)',
	COLOR_BACKGROUND_DISABLED: 'var(--project-color-background-disabled)',
	COLOR_BACKGROUND_INPUT: 'var(--project-background-input)',
	COLOR_BACKGROUND_NEUTRAL: 'var(--project-background-neutral)',
	COLOR_BACKGROUND_NEUTRAL_HOVERED: 'var(--project-background-neutral-hovered)',
	COLOR_BACKGROUND_NEUTRAL_PRESSED: 'var(--project-background-neutral-pressed)',
	COLOR_BACKGROUND_NEUTRAL_SUBTLE_HOVERED: 'var(--project-background-neutral-subtle-hovered)',
	COLOR_BACKGROUND_NEUTRAL_SUBTLE_PRESSED: 'var(--project-background-neutral-subtle-pressed)',
	COLOR_BACKGROUND_SELECTED: 'var(--project-background-selected)',
	COLOR_BORDER: 'var(--project-color-border)',
	COLOR_BORDER_INPUT: 'var(--project-color-border-input)',
	COLOR_BORDER_INVERSE: 'var(--project-color-border-inverse)',
	COLOR_BORDER_SELECTED: 'var(--project-color-border-selected)',
	COLOR_ELEVATION_SURFACE: 'var(--project-color-elevation-surface)',
	COLOR_ELEVATION_SURFACE_SUNKEN: 'var(--project-color-elevation-surface-sunken)',
	COLOR_ICON_DANGER: 'var(--project-color-icon-danger)',
	COLOR_ICON_INFORMATION: 'var(--project-color-icon-information)',
	COLOR_ICON_SUCCESS: 'var(--project-color-icon-success)',
	COLOR_ICON_WARNING: 'var(--project-color-icon-warning)',
	COLOR_LINK: 'var(--project-color-link)',
	COLOR_LINK_PRESSED: 'var(--project-color-link-pressed)',
	COLOR_SKELETON: 'var(--project-skeleton)',
	COLOR_TEXT: 'var(--project-color-text)',
	COLOR_TEXT_ACCENT_SELECTED: 'var(--project-text-accent-selected)',
	COLOR_TEXT_DISABLED: 'var(--project-color-text-disabled)',
	COLOR_TEXT_INVERSE: 'var(--project-color-text-inverse)',
	COLOR_TEXT_SELECTED: 'var(--project-color-text-selected)',
	COLOR_TEXT_SUBTLE: 'var(--project-color-text-subtle)',
	COLOR_TEXT_SUBTLEST: 'var(--project-color-text-subtlest)',
	CONTENT_HEIGHT: 'var(--project-content-height)',
	CONTENT_WIDTH: 'var(--project-content-width)',
	ELEVATION_SURFACE: 'var(--project-elevation-surface)',
	ELEVATION_SURFACE_CONTRAST: 'var(--project-elevation-surface-contrast)',
	ELEVATION_SURFACE_CONTRAST_BOLDER: 'var(--project-elevation-surface-contrast-bolder)',
	ELEVATION_SURFACE_BLEND_MODE: 'var(--project-elevation-surface-blend-mode)',
	WRAPPER_BACKGROUND: 'var(--project-wrapper-background)',
	WRAPPER_BORDER_RADIUS: 'var(--project-wrapper-border-radius)',
	WRAPPER_HEIGHT: 'var(--project-wrapper-height)',
	WRAPPER_PADDING: 'var(--project-wrapper-padding)',
	WRAPPER_WIDTH: 'var(--project-wrapper-width)',
};

export const BASE_THEME: ProjectThemeTokens = {
	'color.background.neutral.subtle.hovered.contrast': token(
		'color.background.neutral.subtle.hovered',
	),
	'color.background.accent.selected': colors.B700,
	'color.background.brand.bold': token('color.background.brand.bold'),
	'color.background.brand.bold.hovered': token('color.background.brand.bold.hovered'),
	'color.background.brand.bold.pressed': token('color.background.brand.bold.pressed'),
	'color.background.disabled': token('color.background.disabled'),
	'color.background.input': token('color.background.input'),
	'color.background.neutral': token('color.background.neutral'),
	'color.background.neutral.hovered': token('color.background.neutral.hovered'),
	'color.background.neutral.pressed': token('color.background.neutral.pressed'),
	'color.background.neutral.subtle.hovered': token('color.background.neutral.subtle.hovered'),
	'color.background.neutral.subtle.pressed': token('color.background.neutral.subtle.pressed'),
	'color.background.selected': token('color.background.selected'),
	'color.elevation.surface': token('elevation.surface'),
	'color.elevation.surface.sunken': token('elevation.surface.sunken'),
	'color.border': token('color.border'),
	'color.border.input': token('color.border.input'),
	'color.border.inverse': token('color.border.inverse'),
	'color.border.selected': token('color.border.selected'),
	'color.icon.danger': token('color.icon.danger'),
	'color.icon.information': token('color.icon.information'),
	'color.icon.success': token('color.icon.success'),
	'color.icon.warning': token('color.icon.warning'),
	'color.link': token('color.link'),
	'color.link.pressed': token('color.link.pressed'),
	'color.skeleton': token('color.background.neutral'),
	'color.text': token('color.text'),
	'color.text.accent.selected': token('color.text'),
	'color.text.disabled': token('color.text.disabled'),
	'color.text.inverse': token('color.text.inverse'),
	'color.text.selected': token('color.text.selected'),
	'color.text.subtle': token('color.text.subtle'),
	'color.text.subtlest': token('color.text.subtlest'),
	'elevation.surface.contrast': 'transparent',
	'elevation.surface.contrast.bolder': 'transparent',
	'elevation.surface.blend.mode': 'darken',
};

export const HIGH_BRIGHTNESS_THEME: PartialProjectThemeTokens = {
	'color.background.accent.selected': token('color.background.neutral.hovered'),
	'color.background.brand.bold': token('color.background.accent.gray.bolder'),
	'color.background.brand.bold.hovered': token('color.background.accent.gray.bolder.hovered'),
	'color.background.brand.bold.pressed': token('color.background.accent.gray.bolder.pressed'),
	'color.background.disabled': token('color.background.neutral'),
	'color.background.input': 'rgba(255, 255, 255, 0.8)',
	'color.background.selected': token('color.background.accent.gray.bolder.pressed'),
	'color.text.selected': token('color.text.inverse'),
};

export const LOW_BRIGHTNESS_THEME: PartialProjectThemeTokens = {
	'color.background.neutral.subtle.hovered.contrast': 'rgba(255, 255, 255, 0.08)',
	'color.background.accent.selected': token('color.background.neutral.hovered'),
	'color.background.brand.bold': 'rgba(255, 255, 255, 0.8)',
	'color.background.brand.bold.hovered': 'rgba(255, 255, 255, 0.6)',
	'color.background.brand.bold.pressed': 'rgba(255, 255, 255, 0.4)',
	'color.background.disabled': 'rgba(255, 255, 255, 0.08)',
	'color.background.input': 'rgba(255, 255, 255, 0.08)',
	'color.background.neutral': 'rgba(255, 255, 255, 0.08)',
	'color.background.neutral.hovered': 'rgba(255, 255, 255, 0.16)',
	'color.background.neutral.pressed': 'rgba(255, 255, 255, 0.28)',
	'color.background.neutral.subtle.hovered': 'rgba(255, 255, 255, 0.08)',
	'color.background.neutral.subtle.pressed': 'rgba(255, 255, 255, 0.16)',
	'color.background.selected': 'rgba(255, 255, 255, 0.6)',
	'color.elevation.surface': '#172b4d',
	'color.border': 'rgba(255, 255, 255, 0.14)',
	'color.border.input': 'rgba(255, 255, 255, 0.3)',
	'color.border.inverse': 'rgba(255, 255, 255, 0.3)',
	'color.border.selected': colors.N0,
	'color.icon.danger': colors.N0,
	'color.icon.information': colors.N0,
	'color.icon.success': colors.N0,
	'color.icon.warning': colors.N0,
	'color.skeleton': 'rgba(255,255,255,0.08)',
	'color.text': colors.N0,
	'color.text.disabled': 'rgba(255, 255, 255, 0.6)',
	'color.text.inverse': '#172B4D', // Neutral 1000
	'color.text.selected': '#172B4D', // Neutral 1000
	'color.text.subtle': 'rgba(255, 255, 255, 0.8)',
	'color.text.subtlest': 'rgba(255, 255, 255, 0.8)',
};

export const GRADIENT_THEME: PartialProjectThemeTokens = {
	'color.link': '#CCE0FF', // Blue 200
	'color.link.pressed': '#E9F2FF', // Blue 100
};

// Image
export const IMAGE_BACKGROUND_CONFIG: ImageBackgroundConfig = {
	name: 'image',
	brightness: 'high',
	theme: {
		light: {
			'color.background.brand.bold': token('color.background.accent.gray.bolder'),
			'color.background.brand.bold.hovered': token('color.background.accent.gray.bolder.hovered'),
			'color.background.brand.bold.pressed': token('color.background.accent.gray.bolder.pressed'),
			'color.background.selected': token('color.background.accent.gray.bolder.pressed'),
			'color.link': token('color.text.accent.blue.bolder'),
			'color.link.pressed': token('color.text.accent.blue'),
			'color.text.selected': token('color.text.inverse'),
			'elevation.surface.contrast': 'rgba(255,255,255,0.82)',
			'elevation.surface.contrast.bolder': token('elevation.surface'),
		},
		dark: {
			'color.background.brand.bold': token('color.background.accent.gray.bolder'),
			'color.background.brand.bold.hovered': token('color.background.accent.gray.bolder.hovered'),
			'color.background.brand.bold.pressed': token('color.background.accent.gray.bolder.pressed'),
			'color.background.input': 'rgba(34, 39, 43, 0.8)',
			'color.background.selected': token('color.background.accent.gray.bolder.pressed'),
			'color.elevation.surface': '#172b4d',
			'color.border': 'rgba(255, 255, 255, 0.14)',
			'color.border.input': 'rgba(255, 255, 255, 0.3)',
			'color.border.inverse': 'rgba(255, 255, 255, 0.3)',
			'color.icon.danger': colors.N0,
			'color.icon.information': colors.N0,
			'color.icon.success': colors.N0,
			'color.icon.warning': colors.N0,
			'color.link': token('color.text.accent.blue.bolder'),
			'color.link.pressed': token('color.text.accent.blue'),
			'color.skeleton': 'rgba(255,255,255,0.8)',
			'color.text': colors.N0,
			'color.text.selected': token('color.text.inverse'),
			'elevation.surface.contrast': 'rgba(29, 33, 37, 0.82)',
			'elevation.surface.contrast.bolder': token('elevation.surface'),
		},
	},
};

// Old Colors
const WHITE_BACKGROUND_CONFIG: OldBackgroundConfig = {
	name: 'white',
};

const LIGHT_PURPLE_BACKGROUND_CONFIG: OldBackgroundConfig = {
	name: 'light-purple',
};

const LIGHT_BLUE_BACKGROUND_CONFIG: OldBackgroundConfig = {
	name: 'light-blue',
};

const LIGHT_TEAL_BACKGROUND_CONFIG: OldBackgroundConfig = {
	name: 'light-teal',
};

const LIGHT_GREEN_BACKGROUND_CONFIG: OldBackgroundConfig = {
	name: 'light-green',
};

const LIGHT_YELLOW_BACKGROUND_CONFIG: OldBackgroundConfig = {
	name: 'light-yellow',
};

const LIGHT_RED_BACKGROUND_CONFIG: OldBackgroundConfig = {
	name: 'light-red',
};

const LIGHT_MAGENTA_BACKGROUND_CONFIG: OldBackgroundConfig = {
	name: 'light-magenta',
};

const LIGHT_ORANGE_BACKGROUND_CONFIG: OldBackgroundConfig = {
	name: 'light-orange',
};

const DARK_BACKGROUND_CONFIG: OldBackgroundConfig = {
	name: 'dark',
};

const PURPLE_BACKGROUND_CONFIG: OldBackgroundConfig = {
	name: 'purple',
};

const BLUE_BACKGROUND_CONFIG: OldBackgroundConfig = {
	name: 'blue',
};

const TEAL_BACKGROUND_CONFIG: OldBackgroundConfig = {
	name: 'teal',
};

const GREEN_BACKGROUND_CONFIG: OldBackgroundConfig = {
	name: 'green',
};

const YELLOW_BACKGROUND_CONFIG: OldBackgroundConfig = {
	name: 'yellow',
};

const RED_BACKGROUND_CONFIG: OldBackgroundConfig = {
	name: 'red',
};

const MAGENTA_BACKGROUND_CONFIG: OldBackgroundConfig = {
	name: 'magenta',
};

const ORANGE_BACKGROUND_CONFIG: OldBackgroundConfig = {
	name: 'orange',
};

// New Colors
const DARKER_GRAY_BACKGROUND_CONFIG: ColorBackgroundConfig = {
	name: 'darker-gray',
	brightness: 'low',
	theme: {
		light: {
			'color.elevation.surface': '#44546F', // Neutral 800
			'color.link': '#F1F2F4', // Neutral 200
			'color.link.pressed': '#F7F8F9', // Neutral 100
		},
		dark: {
			'color.elevation.surface': '#44546F', // Neutral 800
			'color.link': '#F1F2F4', // Neutral 200
			'color.link.pressed': '#F7F8F9', // Neutral 100
		},
	},
};

const DARKER_PURPLE_BACKGROUND_CONFIG: ColorBackgroundConfig = {
	name: 'darker-purple',
	brightness: 'low',
	theme: {
		light: {
			'color.elevation.surface': '#5E4DB2', // Purple 800
			'color.link': '#DFD8FD', // Purple 200
			'color.link.pressed': '#F3F0FF', // Purple 100
		},
		dark: {
			'color.elevation.surface': '#5E4DB2', // Purple 800
			'color.link': '#DFD8FD', // Purple 200
			'color.link.pressed': '#F3F0FF', // Purple 100
		},
	},
};

const DARKER_BLUE_BACKGROUND_CONFIG: ColorBackgroundConfig = {
	name: 'darker-blue',
	brightness: 'low',
	theme: {
		light: {
			'color.elevation.surface': '#0055CC', // Blue 800
			'color.link': '#CCE0FF', // Blue 200
			'color.link.pressed': '#E9F2FF', // Blue 100
		},
		dark: {
			'color.elevation.surface': '#0055CC', // Blue 800
			'color.link': '#CCE0FF', // Blue 200
			'color.link.pressed': '#E9F2FF', // Blue 100
		},
	},
};

const DARKER_TEAL_BACKGROUND_CONFIG: ColorBackgroundConfig = {
	name: 'darker-teal',
	brightness: 'low',
	theme: {
		light: {
			'color.elevation.surface': '#206A83', // Teal 800
			'color.link': '#C6EDFB', // Teal 200
			'color.link.pressed': '#E7F9FF', // Teal 100
		},
		dark: {
			'color.elevation.surface': '#206A83', // Teal 800
			'color.link': '#C6EDFB', // Teal 200
			'color.link.pressed': '#E7F9FF', // Teal 100
		},
	},
};

const DARKER_GREEN_BACKGROUND_CONFIG: ColorBackgroundConfig = {
	name: 'darker-green',
	brightness: 'low',
	theme: {
		light: {
			'color.elevation.surface': '#216E4E', // Green 800
			'color.link': '#BAF3DB', // Green 200
			'color.link.pressed': '#DCFFF1', // Green 100
		},
		dark: {
			'color.elevation.surface': '#216E4E', // Green 800
			'color.link': '#BAF3DB', // Green 200
			'color.link.pressed': '#DCFFF1', // Green 100
		},
	},
};

const DARKER_LIME_BACKGROUND_CONFIG: ColorBackgroundConfig = {
	name: 'darker-lime',
	brightness: 'low',
	theme: {
		light: {
			'color.elevation.surface': '#4C6B1F', // Lime 800
			'color.link': '#D3F1A7', // Lime 200
			'color.link.pressed': '#EFFFD6', // Lime 100
		},
		dark: {
			'color.elevation.surface': '#4C6B1F', // Lime 800
			'color.link': '#D3F1A7', // Lime 200
			'color.link.pressed': '#EFFFD6', // Lime 100
		},
	},
};

const DARKER_YELLOW_BACKGROUND_CONFIG: ColorBackgroundConfig = {
	name: 'darker-yellow',
	brightness: 'low',
	theme: {
		light: {
			'color.elevation.surface': '#7F5F01', // Yellow 800
			'color.link': '#F8E6A0', // Yellow 200
			'color.link.pressed': '#FFF7D6', // Yellow 100
		},
		dark: {
			'color.elevation.surface': '#7F5F01', // Yellow 800
			'color.link': '#F8E6A0', // Yellow 200
			'color.link.pressed': '#FFF7D6', // Yellow 100
		},
	},
};

const DARKER_ORANGE_BACKGROUND_CONFIG: ColorBackgroundConfig = {
	name: 'darker-orange',
	brightness: 'low',
	theme: {
		light: {
			'color.elevation.surface': '#A54800', // Orange 800
			'color.link': '#FEDEC8', // Orange 200
			'color.link.pressed': '#FFF3EB', // Orange 100
		},
		dark: {
			'color.elevation.surface': '#A54800', // Orange 800
			'color.link': '#FEDEC8', // Orange 200
			'color.link.pressed': '#FFF3EB', // Orange 100
		},
	},
};

const DARKER_RED_BACKGROUND_CONFIG: ColorBackgroundConfig = {
	name: 'darker-red',
	brightness: 'low',
	theme: {
		light: {
			'color.elevation.surface': '#AE2E24', // Red 800
			'color.link': '#FFD5D2', // Red 200
			'color.link.pressed': '#FFECEB', // Red 100
		},
		dark: {
			'color.elevation.surface': '#AE2E24', // Red 800
			'color.link': '#FFD5D2', // Red 200
			'color.link.pressed': '#FFECEB', // Red 100
		},
	},
};

const DARKER_MAGENTA_BACKGROUND_CONFIG: ColorBackgroundConfig = {
	name: 'darker-magenta',
	brightness: 'low',
	theme: {
		light: {
			'color.elevation.surface': '#943D73', // Magenta 800
			'color.link': '#FDD0EC', // Magenta 200
			'color.link.pressed': '#FFECF8', // Magenta 100
		},
		dark: {
			'color.elevation.surface': '#943D73', // Magenta 800
			'color.link': '#FDD0EC', // Magenta 200
			'color.link.pressed': '#FFECF8', // Magenta 100
		},
	},
};

const DARKEST_GRAY_BACKGROUND_CONFIG: ColorBackgroundConfig = {
	name: 'darkest-gray',
	brightness: 'low',
	theme: {
		light: {
			'color.elevation.surface': '#172B4D', // Neutral 1000
			'color.link': '#F1F2F4', // Neutral 200
			'color.link.pressed': '#F7F8F9', // Neutral 100
		},
		dark: {
			'color.elevation.surface': '#172B4D', // Neutral 1000
			'color.link': '#F1F2F4', // Neutral 200
			'color.link.pressed': '#F7F8F9', // Neutral 100
		},
	},
};

const DARKEST_PURPLE_BACKGROUND_CONFIG: ColorBackgroundConfig = {
	name: 'darkest-purple',
	brightness: 'low',
	theme: {
		light: {
			'color.elevation.surface': '#352C63', // Purple 900
			'color.link': '#DFD8FD', // Purple 200
			'color.link.pressed': '#F3F0FF', // Purple 100
		},
		dark: {
			'color.elevation.surface': '#352C63', // Purple 900
			'color.link': '#DFD8FD', // Purple 200
			'color.link.pressed': '#F3F0FF', // Purple 100
		},
	},
};

const DARKEST_BLUE_BACKGROUND_CONFIG: ColorBackgroundConfig = {
	name: 'darkest-blue',
	brightness: 'low',
	theme: {
		light: {
			'color.elevation.surface': '#09326C', // Blue 900
			'color.link': '#CCE0FF', // Blue 200
			'color.link.pressed': '#E9F2FF', // Blue 100
		},
		dark: {
			'color.elevation.surface': '#09326C', // Blue 900
			'color.link': '#CCE0FF', // Blue 200
			'color.link.pressed': '#E9F2FF', // Blue 100
		},
	},
};

const DARKEST_TEAL_BACKGROUND_CONFIG: ColorBackgroundConfig = {
	name: 'darkest-teal',
	brightness: 'low',
	theme: {
		light: {
			'color.elevation.surface': '#164555', // Teal 900
			'color.link': '#C6EDFB', // Teal 200
			'color.link.pressed': '#E7F9FF', // Teal 100
		},
		dark: {
			'color.elevation.surface': '#164555', // Teal 900
			'color.link': '#C6EDFB', // Teal 200
			'color.link.pressed': '#E7F9FF', // Teal 100
		},
	},
};

const DARKEST_GREEN_BACKGROUND_CONFIG: ColorBackgroundConfig = {
	name: 'darkest-green',
	brightness: 'low',
	theme: {
		light: {
			'color.elevation.surface': '#164B35', // Green 900
			'color.link': '#BAF3DB', // Green 200
			'color.link.pressed': '#DCFFF1', // Green 100
		},
		dark: {
			'color.elevation.surface': '#164B35', // Green 900
			'color.link': '#BAF3DB', // Green 200
			'color.link.pressed': '#DCFFF1', // Green 100
		},
	},
};

const DARKEST_LIME_BACKGROUND_CONFIG: ColorBackgroundConfig = {
	name: 'darkest-lime',
	brightness: 'low',
	theme: {
		light: {
			'color.elevation.surface': '#37471F', // Lime 900
			'color.link': '#D3F1A7', // Lime 200
			'color.link.pressed': '#EFFFD6', // Lime 100
		},
		dark: {
			'color.elevation.surface': '#37471F', // Lime 900
			'color.link': '#D3F1A7', // Lime 200
			'color.link.pressed': '#EFFFD6', // Lime 100
		},
	},
};

const DARKEST_YELLOW_BACKGROUND_CONFIG: ColorBackgroundConfig = {
	name: 'darkest-yellow',
	brightness: 'low',
	theme: {
		light: {
			'color.elevation.surface': '#533F04', // Yellow 900
			'color.link': '#F8E6A0', // Yellow 200
			'color.link.pressed': '#FFF7D6', // Yellow 100
		},
		dark: {
			'color.elevation.surface': '#533F04', // Yellow 900
			'color.link': '#F8E6A0', // Yellow 200
			'color.link.pressed': '#FFF7D6', // Yellow 100
		},
	},
};

const DARKEST_ORANGE_BACKGROUND_CONFIG: ColorBackgroundConfig = {
	name: 'darkest-orange',
	brightness: 'low',
	theme: {
		light: {
			'color.elevation.surface': '#702E00', // Orange 900
			'color.link': '#FEDEC8', // Orange 200
			'color.link.pressed': '#FFF3EB', // Orange 100
		},
		dark: {
			'color.elevation.surface': '#702E00', // Orange 900
			'color.link': '#FEDEC8', // Orange 200
			'color.link.pressed': '#FFF3EB', // Orange 100
		},
	},
};

const DARKEST_RED_BACKGROUND_CONFIG: ColorBackgroundConfig = {
	name: 'darkest-red',
	brightness: 'low',
	theme: {
		light: {
			'color.elevation.surface': '#5D1F1A', // Red 900
			'color.link': '#FFD5D2', // Red 200
			'color.link.pressed': '#FFECEB', // Red 100
		},
		dark: {
			'color.elevation.surface': '#5D1F1A', // Red 900
			'color.link': '#FFD5D2', // Red 200
			'color.link.pressed': '#FFECEB', // Red 100
		},
	},
};

const DARKEST_MAGENTA_BACKGROUND_CONFIG: ColorBackgroundConfig = {
	name: 'darkest-magenta',
	brightness: 'low',
	theme: {
		light: {
			'color.elevation.surface': '#50253F', // Magenta 900
			'color.link': '#FDD0EC', // Magenta 200
			'color.link.pressed': '#FFECF8', // Magenta 100
		},
		dark: {
			'color.elevation.surface': '#50253F', // Magenta 900
			'color.link': '#FDD0EC', // Magenta 200
			'color.link.pressed': '#FFECF8', // Magenta 100
		},
	},
};

const LIGHTEST_GRAY_BACKGROUND_CONFIG: ColorBackgroundConfig = {
	name: 'lightest-gray',
	brightness: 'high',
	theme: {
		light: {
			'color.elevation.surface': '#F1F2F4', // Neutral 200
			'color.link': '#44546F', // Neutral 800
			'color.link.pressed': '#091E42', // Neutral 1100
		},
		dark: {
			'color.elevation.surface': '#44546F', // Neutral 800
			'color.link': '#F1F2F4', // Neutral 200
			'color.link.pressed': '#F7F8F9', // Neutral 100
		},
	},
};

const LIGHTEST_PURPLE_BACKGROUND_CONFIG: ColorBackgroundConfig = {
	name: 'lightest-purple',
	brightness: 'high',
	theme: {
		light: {
			'color.elevation.surface': '#F3F0FF', // Purple 100
			'color.link': '#5E4DB2', // Purple 800
			'color.link.pressed': '#352C63', // Purple 900
		},
		dark: {
			'color.elevation.surface': '#5E4DB2', // Purple 800
			'color.link': '#DFD8FD', // Purple 200
			'color.link.pressed': '#F3F0FF', // Purple 100
		},
	},
};

const LIGHTEST_BLUE_BACKGROUND_CONFIG: ColorBackgroundConfig = {
	name: 'lightest-blue',
	brightness: 'high',
	theme: {
		light: {
			'color.elevation.surface': '#E9F2FF', // Blue 100
			'color.link': '#0055CC', // Blue 800
			'color.link.pressed': '#09326C', // Blue 900
		},
		dark: {
			'color.elevation.surface': '#0055CC', // Blue 800
			'color.link': '#CCE0FF', // Blue 200
			'color.link.pressed': '#E9F2FF', // Blue 100
		},
	},
};

const LIGHTEST_TEAL_BACKGROUND_CONFIG: ColorBackgroundConfig = {
	name: 'lightest-teal',
	brightness: 'high',
	theme: {
		light: {
			'color.elevation.surface': '#E7F9FF', // Teal 100
			'color.link': '#206A83', // Teal 800
			'color.link.pressed': '#164555', // Teal 900
		},
		dark: {
			'color.elevation.surface': '#206A83', // Teal 800
			'color.link': '#C6EDFB', // Teal 200
			'color.link.pressed': '#E7F9FF', // Teal 100
		},
	},
};

const LIGHTEST_GREEN_BACKGROUND_CONFIG: ColorBackgroundConfig = {
	name: 'lightest-green',
	brightness: 'high',
	theme: {
		light: {
			'color.elevation.surface': '#DCFFF1', // Green 100
			'color.link': '#216E4E', // Green 800
			'color.link.pressed': '#164B35', // Green 900
		},
		dark: {
			'color.elevation.surface': '#216E4E', // Green 800
			'color.link': '#BAF3DB', // Green 200
			'color.link.pressed': '#DCFFF1', // Green 100
		},
	},
};

const LIGHTEST_LIME_BACKGROUND_CONFIG: ColorBackgroundConfig = {
	name: 'lightest-lime',
	brightness: 'high',
	theme: {
		light: {
			'color.elevation.surface': '#EFFFD6', // Lime 100
			'color.link': '#4C6B1F', // Lime 800
			'color.link.pressed': '#37471F', // Lime 900
		},
		dark: {
			'color.elevation.surface': '#4C6B1F', // Lime 800
			'color.link': '#D3F1A7', // Lime 200
			'color.link.pressed': '#EFFFD6', // Lime 100
		},
	},
};

const LIGHTEST_YELLOW_BACKGROUND_CONFIG: ColorBackgroundConfig = {
	name: 'lightest-yellow',
	brightness: 'high',
	theme: {
		light: {
			'color.elevation.surface': '#FFF7D6', // Yellow 100
			'color.link': '#7F5F01', // Yellow 800
			'color.link.pressed': '#533F04', // Yellow 900
		},
		dark: {
			'color.elevation.surface': '#7F5F01', // Yellow 800
			'color.link': '#F8E6A0', // Yellow 200
			'color.link.pressed': '#FFF7D6', // Yellow 100
		},
	},
};

const LIGHTEST_ORANGE_BACKGROUND_CONFIG: ColorBackgroundConfig = {
	name: 'lightest-orange',
	brightness: 'high',
	theme: {
		light: {
			'color.elevation.surface': '#FFF3EB', // Orange 100
			'color.link': '#A54800', // Orange 800
			'color.link.pressed': '#702E00', // Orange 900
		},
		dark: {
			'color.elevation.surface': '#A54800', // Orange 800
			'color.link': '#FEDEC8', // Orange 200
			'color.link.pressed': '#FFF3EB', // Orange 100
		},
	},
};

const LIGHTEST_RED_BACKGROUND_CONFIG: ColorBackgroundConfig = {
	name: 'lightest-red',
	brightness: 'high',
	theme: {
		light: {
			'color.elevation.surface': '#FFECEB', // Red 100
			'color.link': '#AE2E24', // Red 800
			'color.link.pressed': '#5D1F1A', // Red 900
		},
		dark: {
			'color.elevation.surface': '#AE2E24', // Red 800
			'color.link': '#FFD5D2', // Red 200
			'color.link.pressed': '#FFECEB', // Red 100
		},
	},
};

const LIGHTEST_MAGENTA_BACKGROUND_CONFIG: ColorBackgroundConfig = {
	name: 'lightest-magenta',
	brightness: 'high',
	theme: {
		light: {
			'color.elevation.surface': '#FFECF8', // Magenta 100
			'color.link': '#943D73', // Magenta 800
			'color.link.pressed': '#50253F', // Magenta 900
		},
		dark: {
			'color.elevation.surface': '#943D73', // Magenta 800
			'color.link': '#FDD0EC', // Magenta 200
			'color.link.pressed': '#FFECF8', // Magenta 100
		},
	},
};

const LIGHTER_GRAY_BACKGROUND_CONFIG: ColorBackgroundConfig = {
	name: 'lighter-gray',
	brightness: 'high',
	theme: {
		light: {
			'color.elevation.surface': '#DCDFE4', // Neutral 200
			'color.link': '#44546F', // Neutral 800
			'color.link.pressed': '#091E42', // Neutral 1100
		},
		dark: {
			'color.elevation.surface': '#172B4D', // Neutral 1000
			'color.link': '#F1F2F4', // Neutral 200
			'color.link.pressed': '#F7F8F9', // Neutral 100
		},
	},
};

const LIGHTER_PURPLE_BACKGROUND_CONFIG: ColorBackgroundConfig = {
	name: 'lighter-purple',
	brightness: 'high',
	theme: {
		light: {
			'color.elevation.surface': '#DFD8FD', // Purple 200
			'color.link': '#5E4DB2', // Purple 800
			'color.link.pressed': '#352C63', // Purple 900
		},
		dark: {
			'color.elevation.surface': '#352C63', // Purple 900
			'color.link': '#DFD8FD', // Purple 200
			'color.link.pressed': '#F3F0FF', // Purple 100
		},
	},
};

const LIGHTER_BLUE_BACKGROUND_CONFIG: ColorBackgroundConfig = {
	name: 'lighter-blue',
	brightness: 'high',
	theme: {
		light: {
			'color.elevation.surface': '#CCE0FF', // Blue 200
			'color.link': '#0055CC', // Blue 800
			'color.link.pressed': '#09326C', // Blue 900
		},
		dark: {
			'color.elevation.surface': '#09326C', // Blue 900
			'color.link': '#CCE0FF', // Blue 200
			'color.link.pressed': '#E9F2FF', // Blue 100
		},
	},
};

const LIGHTER_TEAL_BACKGROUND_CONFIG: ColorBackgroundConfig = {
	name: 'lighter-teal',
	brightness: 'high',
	theme: {
		light: {
			'color.elevation.surface': '#C6EDFB', // Teal 200
			'color.link': '#206A83', // Teal 800
			'color.link.pressed': '#164555', // Teal 900
		},
		dark: {
			'color.elevation.surface': '#164555', // Teal 900
			'color.link': '#C6EDFB', // Teal 200
			'color.link.pressed': '#E7F9FF', // Teal 100
		},
	},
};

const LIGHTER_GREEN_BACKGROUND_CONFIG: ColorBackgroundConfig = {
	name: 'lighter-green',
	brightness: 'high',
	theme: {
		light: {
			'color.elevation.surface': '#BAF3DB', // Green 200
			'color.link': '#216E4E', // Green 800
			'color.link.pressed': '#164B35', // Green 900
		},
		dark: {
			'color.elevation.surface': '#164B35', // Green 900
			'color.link': '#BAF3DB', // Green 200
			'color.link.pressed': '#DCFFF1', // Green 100
		},
	},
};

const LIGHTER_LIME_BACKGROUND_CONFIG: ColorBackgroundConfig = {
	name: 'lighter-lime',
	brightness: 'high',
	theme: {
		light: {
			'color.elevation.surface': '#D3F1A7', // Lime 200
			'color.link': '#4C6B1F', // Lime 800
			'color.link.pressed': '#37471F', // Lime 900
		},
		dark: {
			'color.elevation.surface': '#37471F', // Lime 900
			'color.link': '#D3F1A7', // Lime 200
			'color.link.pressed': '#EFFFD6', // Lime 100
		},
	},
};

const LIGHTER_YELLOW_BACKGROUND_CONFIG: ColorBackgroundConfig = {
	name: 'lighter-yellow',
	brightness: 'high',
	theme: {
		light: {
			'color.elevation.surface': '#F8E6A0', // Yellow 200
			'color.link': '#7F5F01', // Yellow 800
			'color.link.pressed': '#533F04', // Yellow 900
		},
		dark: {
			'color.elevation.surface': '#533F04', // Yellow 900
			'color.link': '#F8E6A0', // Yellow 200
			'color.link.pressed': '#FFF7D6', // Yellow 100
		},
	},
};

const LIGHTER_ORANGE_BACKGROUND_CONFIG: ColorBackgroundConfig = {
	name: 'lighter-orange',
	brightness: 'high',
	theme: {
		light: {
			'color.elevation.surface': '#FEDEC8', // Orange 200
			'color.link': '#A54800', // Orange 800
			'color.link.pressed': '#702E00', // Orange 900
		},
		dark: {
			'color.elevation.surface': '#702E00', // Orange 900
			'color.link': '#FEDEC8', // Orange 200
			'color.link.pressed': '#FFF3EB', // Orange 100
		},
	},
};

const LIGHTER_RED_BACKGROUND_CONFIG: ColorBackgroundConfig = {
	name: 'lighter-red',
	brightness: 'high',
	theme: {
		light: {
			'color.elevation.surface': '#FFD5D2', // Red 200
			'color.link': '#AE2E24', // Red 800
			'color.link.pressed': '#5D1F1A', // Red 900
		},
		dark: {
			'color.elevation.surface': '#5D1F1A', // Red 900
			'color.link': '#FFD5D2', // Red 200
			'color.link.pressed': '#FFECEB', // Red 100
		},
	},
};

const LIGHTER_MAGENTA_BACKGROUND_CONFIG: ColorBackgroundConfig = {
	name: 'lighter-magenta',
	brightness: 'high',
	theme: {
		light: {
			'color.elevation.surface': '#FDD0EC', // Magenta 200
			'color.link': '#943D73', // Magenta 800
			'color.link.pressed': '#50253F', // Magenta 900
		},
		dark: {
			'color.elevation.surface': '#50253F', // Magenta 900
			'color.link': '#FDD0EC', // Magenta 200
			'color.link.pressed': '#FFECF8', // Magenta 100
		},
	},
};

export const NO_BACKGROUND_CONFIG: ColorBackgroundConfig = {
	name: 'no-background',
	brightness: 'high',
	theme: {
		light: BASE_THEME,
		dark: {
			...BASE_THEME,
			'color.text.accent.selected': token('color.text.inverse'),
		},
	},
};

const ADAPTIVE_CONFIG_BASE: BaseThemeTokens = {
	'color.elevation.surface': token('elevation.surface.hovered'),
	'color.background.input': token('color.background.neutral'),
	'color.link': token('color.text'),
	'color.link.pressed': token('color.text'),
	'color.elevation.surface.sunken': token('elevation.surface.sunken'),
	'color.background.selected': token('color.background.neutral.hovered'),
	'color.background.neutral.subtle.hovered.contrast': token('elevation.surface.hovered'),
	'color.border.selected': token('color.border.bold'),
	'color.text.selected': token('color.text'),
	'color.text': token('color.text'),
	'color.background.brand.bold': token('color.background.neutral.bold'),
	'color.background.brand.bold.hovered': token('color.background.neutral.bold.hovered'),
	'color.background.brand.bold.pressed': token('color.background.neutral.bold.pressed'),
};

export const createAdaptiveColorConfig = ({
	name,
	overrides,
}: {
	name: AdaptiveColorThemeName;
	overrides: Partial<AdaptiveColorBackgroundConfig['theme']['light']>;
}): AdaptiveColorBackgroundConfig => {
	const adaptiveColorTheme = {
		...ADAPTIVE_CONFIG_BASE,
		...overrides,
	};

	return {
		name,
		brightness: 'high',
		theme: {
			light: adaptiveColorTheme,
			dark: adaptiveColorTheme,
		},
	};
};

export const createAdaptiveGradientConfig = ({
	name,
	overridesAllThemes,
	overridesLightTheme,
	overridesDarkTheme,
}: {
	name: AdaptiveGradientThemeName;
	overridesAllThemes: Partial<AdaptiveGradientBackgroundConfig['theme']['light']>;
	overridesLightTheme: Partial<AdaptiveGradientBackgroundConfig['theme']['light']>;
	overridesDarkTheme: Partial<AdaptiveGradientBackgroundConfig['theme']['dark']>;
}): AdaptiveGradientBackgroundConfig => {
	const adaptiveGradientTheme = {
		'elevation.surface': token('elevation.surface'),
		...ADAPTIVE_CONFIG_BASE,
		...overridesAllThemes,
	};

	return {
		name,
		brightness: 'high',
		theme: {
			light: {
				...adaptiveGradientTheme,
				...overridesLightTheme,
			},
			dark: { ...adaptiveGradientTheme, ...overridesDarkTheme },
		},
	};
};

export const ADAPTIVE_IMAGE_BACKGROUND_CONFIG: ImageBackgroundConfig = {
	name: 'image',
	brightness: 'high',
	theme: {
		light: {
			...ADAPTIVE_CONFIG_BASE,
			'elevation.surface.blend.mode': 'normal',
			'elevation.surface.contrast': 'rgba(255,255,255,0.82)',
		},
		dark: {
			...ADAPTIVE_CONFIG_BASE,
			'elevation.surface.blend.mode': 'darken',
			'elevation.surface.contrast': 'rgba(29, 33, 37, 0.82)',
		},
	},
};

export const ADAPTIVE_GRAY_BACKGROUND_CONFIG: AdaptiveColorBackgroundConfig =
	createAdaptiveColorConfig({
		name: 'adaptive-gray',
		overrides: {
			'color.elevation.surface.sunken': token('elevation.surface.pressed'),
		},
	});
export const ADAPTIVE_PURPLE_BACKGROUND_CONFIG: AdaptiveColorBackgroundConfig =
	createAdaptiveColorConfig({
		name: 'adaptive-purple',
		overrides: {
			'color.elevation.surface': token('color.background.accent.purple.subtlest'),
			'color.link': token('color.text.accent.purple'),
			'color.link.pressed': token('color.text.accent.purple'),
			'color.elevation.surface.sunken': token('color.background.accent.purple.subtler'),
		},
	});
export const ADAPTIVE_BLUE_BACKGROUND_CONFIG: AdaptiveColorBackgroundConfig =
	createAdaptiveColorConfig({
		name: 'adaptive-blue',
		overrides: {
			'color.elevation.surface': token('color.background.accent.blue.subtlest'),
			'color.link': token('color.text.accent.blue'),
			'color.link.pressed': token('color.text.accent.blue'),
			'color.elevation.surface.sunken': token('color.background.accent.blue.subtler'),
		},
	});
export const ADAPTIVE_TEAL_BACKGROUND_CONFIG: AdaptiveColorBackgroundConfig =
	createAdaptiveColorConfig({
		name: 'adaptive-teal',
		overrides: {
			'color.elevation.surface': token('color.background.accent.teal.subtlest'),
			'color.link': token('color.text.accent.teal'),
			'color.link.pressed': token('color.text.accent.teal'),
			'color.elevation.surface.sunken': token('color.background.accent.teal.subtler'),
		},
	});
export const ADAPTIVE_GREEN_BACKGROUND_CONFIG: AdaptiveColorBackgroundConfig =
	createAdaptiveColorConfig({
		name: 'adaptive-green',
		overrides: {
			'color.elevation.surface': token('color.background.accent.green.subtlest'),
			'color.link': token('color.text.accent.green'),
			'color.link.pressed': token('color.text.accent.green'),
			'color.elevation.surface.sunken': token('color.background.accent.green.subtler'),
		},
	});
export const ADAPTIVE_MAGENTA_BACKGROUND_CONFIG: AdaptiveColorBackgroundConfig =
	createAdaptiveColorConfig({
		name: 'adaptive-magenta',
		overrides: {
			'color.elevation.surface': token('color.background.accent.magenta.subtlest'),
			'color.link': token('color.text.accent.magenta'),
			'color.link.pressed': token('color.text.accent.magenta'),
			'color.elevation.surface.sunken': token('color.background.accent.magenta.subtler'),
		},
	});

// Gradients
const GRADIENT_GRAY_TO_BLACK_META: GradientBackgroundConfig = {
	name: 'gradient-gray-to-black',
	brightness: 'low',
	theme: {
		light: {
			'color.elevation.surface': '#44546F', // Neutral 800
			'elevation.surface': 'linear-gradient(135deg, #0B1D3A 0%, #505F79 100%)',
		},
		dark: {
			'color.elevation.surface': '#44546F', // Neutral 800
			'elevation.surface': 'linear-gradient(135deg, #0B1D3A 0%, #505F79 100%)',
		},
	},
};

const GRADIENT_DARK_TO_MEDIUM_PURPLE_META: GradientBackgroundConfig = {
	name: 'gradient-dark-to-medium-purple',
	brightness: 'low',
	theme: {
		light: {
			'color.elevation.surface': '#44546F', // Neutral 800
			'elevation.surface': 'linear-gradient(135deg, #002D6D 0%, #61013C 100%)',
		},
		dark: {
			'color.elevation.surface': '#44546F', // Neutral 800
			'elevation.surface': 'linear-gradient(135deg, #002D6D 0%, #61013C 100%)',
		},
	},
};

const GRADIENT_LIGHT_TO_DARK_BLUE_META: GradientBackgroundConfig = {
	name: 'gradient-light-to-dark-blue',
	brightness: 'low',
	theme: {
		light: {
			'color.elevation.surface': '#44546F', // Neutral 800
			'elevation.surface': 'linear-gradient(135deg, #001F4A 0%, #0033B7 100%)',
		},
		dark: {
			'color.elevation.surface': '#44546F', // Neutral 800
			'elevation.surface': 'linear-gradient(135deg, #001F4A 0%, #0033B7 100%)',
		},
	},
};

const GRADIENT_BLUE_TO_TEAL_META: GradientBackgroundConfig = {
	name: 'gradient-blue-to-teal',
	brightness: 'low',
	theme: {
		light: {
			'color.elevation.surface': '#44546F', // Neutral 800
			'elevation.surface': 'linear-gradient(135deg, #000D81 0%, #1492A3 100%)',
		},
		dark: {
			'color.elevation.surface': '#44546F', // Neutral 800
			'elevation.surface': 'linear-gradient(135deg, #000D81 0%, #1492A3 100%)',
		},
	},
};

const GRADIENT_PURPLE_TO_MAGENTA_META: GradientBackgroundConfig = {
	name: 'gradient-purple-to-magenta',
	brightness: 'low',
	theme: {
		light: {
			'color.elevation.surface': '#44546F', // Neutral 800
			'elevation.surface': 'linear-gradient(135deg, #13006F 0%, #CD519D 100%)',
		},
		dark: {
			'color.elevation.surface': '#44546F', // Neutral 800
			'elevation.surface': 'linear-gradient(135deg, #13006F 0%, #CD519D 100%)',
		},
	},
};

const GRADIENT_MAGENTA_TO_ORANGE_META: GradientBackgroundConfig = {
	name: 'gradient-magenta-to-orange',
	brightness: 'low',
	theme: {
		light: {
			'color.elevation.surface': '#44546F', // Neutral 800
			'elevation.surface': 'linear-gradient(135deg, #7D004B 28.5%, #BF3900 100%)',
		},
		dark: {
			'color.elevation.surface': '#44546F', // Neutral 800
			'elevation.surface': 'linear-gradient(135deg, #7D004B 28.5%, #BF3900 100%)',
		},
	},
};

const GRADIENT_DARK_TO_LIGHT_RED_META: GradientBackgroundConfig = {
	name: 'gradient-dark-to-light-red',
	brightness: 'low',
	theme: {
		light: {
			'color.elevation.surface': '#44546F', // Neutral 800
			'elevation.surface': 'linear-gradient(135deg, #150000 0%, #CA0000 100%)',
		},
		dark: {
			'color.elevation.surface': '#44546F', // Neutral 800
			'elevation.surface': 'linear-gradient(135deg, #150000 0%, #CA0000 100%)',
		},
	},
};

const GRADIENT_DARK_TO_LIGHT_ORANGE_META: GradientBackgroundConfig = {
	name: 'gradient-dark-to-light-orange',
	brightness: 'low',
	theme: {
		light: {
			'color.elevation.surface': '#44546F', // Neutral 800
			'elevation.surface': 'linear-gradient(135deg, #650101 0%, #C75300 99.75%)',
		},
		dark: {
			'color.elevation.surface': '#44546F', // Neutral 800
			'elevation.surface': 'linear-gradient(135deg, #650101 0%, #C75300 99.75%)',
		},
	},
};

const GRADIENT_DARK_GREEN_TO_LIME_META: GradientBackgroundConfig = {
	name: 'gradient-dark-green-to-lime',
	brightness: 'low',
	theme: {
		light: {
			'color.elevation.surface': '#133911',
			'elevation.surface': 'linear-gradient(135deg, #072C14 0%, #457200 100%)',
		},
		dark: {
			'color.elevation.surface': '#133911',
			'elevation.surface': 'linear-gradient(135deg, #072C14 0%, #457200 100%)',
		},
	},
};

const GRADIENT_DARK_TO_MEDIUM_TEAL_META: GradientBackgroundConfig = {
	name: 'gradient-dark-to-medium-teal',
	brightness: 'low',
	theme: {
		light: {
			'color.elevation.surface': '#01233D',
			'elevation.surface': 'linear-gradient(135deg, #00152D 0%, #02607E 100%)',
		},
		dark: {
			'color.elevation.surface': '#01233D',
			'elevation.surface': 'linear-gradient(135deg, #00152D 0%, #02607E 100%)',
		},
	},
};

const ADAPTIVE_GRADIENT_GRAY_META: AdaptiveGradientBackgroundConfig = createAdaptiveGradientConfig({
	name: 'adaptive-gradient-gray',
	overridesAllThemes: {},
	overridesLightTheme: {
		'elevation.surface': 'linear-gradient(135deg, #F8F8F8 0%, #B7B9BE 100%)',
	},
	overridesDarkTheme: {
		'elevation.surface': 'linear-gradient(135deg, #292A2E 0%, #6B6E76 100%)',
	},
});

const ADAPTIVE_GRADIENT_PURPLE_META: AdaptiveGradientBackgroundConfig =
	createAdaptiveGradientConfig({
		name: 'adaptive-gradient-purple',
		overridesAllThemes: {
			'color.link': token('color.text.accent.blue'),
			'color.link.pressed': token('color.text.accent.blue'),
			'color.elevation.surface': token('color.background.accent.blue.subtlest.hovered'),
			'color.elevation.surface.sunken': token('color.background.accent.blue.subtlest'),
		},
		overridesLightTheme: {
			'elevation.surface': 'linear-gradient(135deg, #CFE1FD 0%, #EED7FC 100%)',
		},
		overridesDarkTheme: {
			'elevation.surface': 'linear-gradient(135deg, #123263 0%, #48245D 100%)',
		},
	});

const ADAPTIVE_GRADIENT_BLUE_META: AdaptiveGradientBackgroundConfig = createAdaptiveGradientConfig({
	name: 'adaptive-gradient-blue',
	overridesAllThemes: {
		'color.link': token('color.text.accent.blue'),
		'color.link.pressed': token('color.text.accent.blue'),
		'color.elevation.surface': token('color.background.accent.blue.subtlest.hovered'),
		'color.elevation.surface.sunken': token('color.background.accent.blue.subtlest'),
	},
	overridesLightTheme: {
		'elevation.surface': 'linear-gradient(135deg, #E9F2FE 0%, #669DF1 100%)',
	},
	overridesDarkTheme: {
		'elevation.surface': 'linear-gradient(135deg, #1C2B42 0%, #1868DB 100%)',
	},
});

const ADAPTIVE_GRADIENT_TEAL_META: AdaptiveGradientBackgroundConfig = createAdaptiveGradientConfig({
	name: 'adaptive-gradient-teal',
	overridesAllThemes: {
		'color.link': token('color.text.accent.teal'),
		'color.link.pressed': token('color.text.accent.teal'),
		'color.elevation.surface': token('color.background.accent.teal.subtlest.hovered'),
		'color.elevation.surface.sunken': token('color.background.accent.teal.subtlest'),
	},
	overridesLightTheme: {
		'elevation.surface': 'linear-gradient(135deg, #E7F9FF 0%, #6CC3E0 100%)',
	},
	overridesDarkTheme: {
		'elevation.surface': 'linear-gradient(135deg, #1E3137 0%, #227D9B 100%)',
	},
});

const ADAPTIVE_GRADIENT_GREEN_META: AdaptiveGradientBackgroundConfig = createAdaptiveGradientConfig(
	{
		name: 'adaptive-gradient-green',
		overridesAllThemes: {
			'color.link': token('color.text.accent.green'),
			'color.link.pressed': token('color.text.accent.green'),
			'color.elevation.surface': token('color.background.accent.green.subtlest.hovered'),
			'color.elevation.surface.sunken': token('color.background.accent.green.subtlest'),
		},
		overridesLightTheme: {
			'elevation.surface': 'linear-gradient(135deg, #DCFFF1 0%, #4BCE97 100%)',
		},
		overridesDarkTheme: {
			'elevation.surface': 'linear-gradient(135deg, #1C3329 0%, #1F845A 100%)',
		},
	},
);

const ADAPTIVE_GRADIENT_MAGENTA_META: AdaptiveGradientBackgroundConfig =
	createAdaptiveGradientConfig({
		name: 'adaptive-gradient-magenta',
		overridesAllThemes: {
			'color.link': token('color.text.accent.magenta'),
			'color.link.pressed': token('color.text.accent.magenta'),
			'color.elevation.surface': token('color.background.accent.magenta.subtlest.hovered'),
			'color.elevation.surface.sunken': token('color.background.accent.magenta.subtlest'),
		},
		overridesLightTheme: {
			'elevation.surface': 'linear-gradient(135deg, #FDD0EC 28.5%, #FD9891 100%)',
		},
		overridesDarkTheme: {
			'elevation.surface': 'linear-gradient(135deg, #50253F 28.5%, #AE2E24 100%)',
		},
	});

// TODO https://jbusiness.atlassian.net/browse/JWMFRONT-947 remove old themes when backend migration is complete
export const BACKGROUND_CONFIGS_OLD = [
	WHITE_BACKGROUND_CONFIG,
	LIGHT_PURPLE_BACKGROUND_CONFIG,
	LIGHT_BLUE_BACKGROUND_CONFIG,
	LIGHT_TEAL_BACKGROUND_CONFIG,
	LIGHT_GREEN_BACKGROUND_CONFIG,
	LIGHT_YELLOW_BACKGROUND_CONFIG,
	LIGHT_ORANGE_BACKGROUND_CONFIG,
	LIGHT_RED_BACKGROUND_CONFIG,
	LIGHT_MAGENTA_BACKGROUND_CONFIG,
	DARK_BACKGROUND_CONFIG,
	PURPLE_BACKGROUND_CONFIG,
	BLUE_BACKGROUND_CONFIG,
	TEAL_BACKGROUND_CONFIG,
	GREEN_BACKGROUND_CONFIG,
	YELLOW_BACKGROUND_CONFIG,
	ORANGE_BACKGROUND_CONFIG,
	RED_BACKGROUND_CONFIG,
	MAGENTA_BACKGROUND_CONFIG,
];

// Solid background colors available in the non-adaptive version of the background palette
// (This palette has a wider selection of gradients/colors, and solid colors do not adapt to dark/light mode)
export const FIXED_COLOR_BACKGROUND_CONFIGS = [
	LIGHTEST_GRAY_BACKGROUND_CONFIG,
	LIGHTEST_PURPLE_BACKGROUND_CONFIG,
	LIGHTEST_BLUE_BACKGROUND_CONFIG,
	LIGHTEST_TEAL_BACKGROUND_CONFIG,
	LIGHTEST_GREEN_BACKGROUND_CONFIG,
	LIGHTEST_LIME_BACKGROUND_CONFIG,
	LIGHTEST_YELLOW_BACKGROUND_CONFIG,
	LIGHTEST_ORANGE_BACKGROUND_CONFIG,
	LIGHTEST_RED_BACKGROUND_CONFIG,
	LIGHTEST_MAGENTA_BACKGROUND_CONFIG,
	LIGHTER_GRAY_BACKGROUND_CONFIG,
	LIGHTER_PURPLE_BACKGROUND_CONFIG,
	LIGHTER_BLUE_BACKGROUND_CONFIG,
	LIGHTER_TEAL_BACKGROUND_CONFIG,
	LIGHTER_GREEN_BACKGROUND_CONFIG,
	LIGHTER_LIME_BACKGROUND_CONFIG,
	LIGHTER_YELLOW_BACKGROUND_CONFIG,
	LIGHTER_ORANGE_BACKGROUND_CONFIG,
	LIGHTER_RED_BACKGROUND_CONFIG,
	LIGHTER_MAGENTA_BACKGROUND_CONFIG,
	DARKER_GRAY_BACKGROUND_CONFIG,
	DARKER_PURPLE_BACKGROUND_CONFIG,
	DARKER_BLUE_BACKGROUND_CONFIG,
	DARKER_TEAL_BACKGROUND_CONFIG,
	DARKER_GREEN_BACKGROUND_CONFIG,
	DARKER_LIME_BACKGROUND_CONFIG,
	DARKER_YELLOW_BACKGROUND_CONFIG,
	DARKER_ORANGE_BACKGROUND_CONFIG,
	DARKER_RED_BACKGROUND_CONFIG,
	DARKER_MAGENTA_BACKGROUND_CONFIG,
	DARKEST_GRAY_BACKGROUND_CONFIG,
	DARKEST_PURPLE_BACKGROUND_CONFIG,
	DARKEST_BLUE_BACKGROUND_CONFIG,
	DARKEST_TEAL_BACKGROUND_CONFIG,
	DARKEST_GREEN_BACKGROUND_CONFIG,
	DARKEST_LIME_BACKGROUND_CONFIG,
	DARKEST_YELLOW_BACKGROUND_CONFIG,
	DARKEST_ORANGE_BACKGROUND_CONFIG,
	DARKEST_RED_BACKGROUND_CONFIG,
	DARKEST_MAGENTA_BACKGROUND_CONFIG,
];

// Solid background colors available in the adaptive version of the background palette
// (This palette has a smaller selection of gradients/colors, and solid colors adapt to dark/light mode)
export const ADAPTIVE_COLOR_BACKGROUND_CONFIGS = [
	ADAPTIVE_GRAY_BACKGROUND_CONFIG,
	ADAPTIVE_PURPLE_BACKGROUND_CONFIG,
	ADAPTIVE_BLUE_BACKGROUND_CONFIG,
	ADAPTIVE_TEAL_BACKGROUND_CONFIG,
	ADAPTIVE_GREEN_BACKGROUND_CONFIG,
	ADAPTIVE_MAGENTA_BACKGROUND_CONFIG,
];

// These gradients are available in the non-adaptive version of the background picker
// (This palette has a wider selection of gradients/colors, and solid colors do not adapt to dark/light mode)
export const GRADIENT_BACKGROUND_CONFIGS = [
	GRADIENT_DARK_TO_LIGHT_RED_META,
	GRADIENT_MAGENTA_TO_ORANGE_META,
	GRADIENT_DARK_TO_LIGHT_ORANGE_META,
	GRADIENT_DARK_GREEN_TO_LIME_META,
	GRADIENT_BLUE_TO_TEAL_META,
	GRADIENT_DARK_TO_MEDIUM_TEAL_META,
	GRADIENT_LIGHT_TO_DARK_BLUE_META,
	GRADIENT_DARK_TO_MEDIUM_PURPLE_META,
	GRADIENT_PURPLE_TO_MAGENTA_META,
	GRADIENT_GRAY_TO_BLACK_META,
];

// Gradients available in the adaptive version of the background palette
// (This palette has a smaller selection of gradients/colors, and solid colors adapt to dark/light mode)
export const ADAPTIVE_GRADIENT_BACKGROUND_CONFIGS = [
	ADAPTIVE_GRADIENT_GRAY_META,
	ADAPTIVE_GRADIENT_PURPLE_META,
	ADAPTIVE_GRADIENT_BLUE_META,
	ADAPTIVE_GRADIENT_TEAL_META,
	ADAPTIVE_GRADIENT_GREEN_META,
	ADAPTIVE_GRADIENT_MAGENTA_META,
];

// TODO https://jbusiness.atlassian.net/browse/JWMFRONT-947 remove old theme mappings when backend migration is complete
export const OLD_TO_NEW_BACKGROUND_CONFIG_MAP: Record<OldThemeName, ColorBackgroundConfig> = {
	white: NO_BACKGROUND_CONFIG,
	'light-purple': LIGHTEST_PURPLE_BACKGROUND_CONFIG,
	'light-blue': LIGHTEST_BLUE_BACKGROUND_CONFIG,
	'light-teal': LIGHTEST_TEAL_BACKGROUND_CONFIG,
	'light-green': LIGHTEST_GREEN_BACKGROUND_CONFIG,
	'light-yellow': LIGHTEST_YELLOW_BACKGROUND_CONFIG,
	'light-orange': LIGHTEST_ORANGE_BACKGROUND_CONFIG,
	'light-red': LIGHTEST_RED_BACKGROUND_CONFIG,
	'light-magenta': LIGHTEST_MAGENTA_BACKGROUND_CONFIG,
	dark: DARKEST_GRAY_BACKGROUND_CONFIG,
	purple: DARKER_PURPLE_BACKGROUND_CONFIG,
	blue: DARKER_BLUE_BACKGROUND_CONFIG,
	teal: DARKER_TEAL_BACKGROUND_CONFIG,
	green: DARKER_GREEN_BACKGROUND_CONFIG,
	yellow: DARKER_YELLOW_BACKGROUND_CONFIG,
	orange: DARKER_ORANGE_BACKGROUND_CONFIG,
	red: DARKER_RED_BACKGROUND_CONFIG,
	magenta: DARKER_MAGENTA_BACKGROUND_CONFIG,
};

// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
export const DEFAULT_BACKGROUND_CONFIG = NO_BACKGROUND_CONFIG as ColorBackgroundConfig;

export const BUSINESS_WRAPPER_PADDING = 24;

export const HORIZONTAL_NAV_HEIGHT = 95;
export const BUSINESS_NAV_HEIGHT = `${HORIZONTAL_NAV_HEIGHT}px`;
export const BUSINESS_WRAPPER_HEIGHT = `calc(100vh - ${PAGE_LAYOUT_OFFSET_TOP} - ${BUSINESS_NAV_HEIGHT})`;
export const BUSINESS_WRAPPER_WITH_BACKGROUND_HEIGHT = `calc(100vh - ${PAGE_LAYOUT_OFFSET_TOP} - ${BUSINESS_NAV_HEIGHT} - ${
	BUSINESS_WRAPPER_PADDING * 2
}px)`;

// nav4 size, but has new height for nav tab
export const NAV4_HORIZONTAL_NAV_HEIGHT = 104;
export const NAV4_BUSINESS_NAV_HEIGHT = `${NAV4_HORIZONTAL_NAV_HEIGHT}px`;
export const NAV4_BUSINESS_WRAPPER_HEIGHT = `calc(100vh - ${PAGE_LAYOUT_OFFSET_TOP} - ${NAV4_BUSINESS_NAV_HEIGHT})`;
export const NAV4_BUSINESS_WRAPPER_WITH_BACKGROUND_HEIGHT = `calc(100vh - ${PAGE_LAYOUT_OFFSET_TOP} - ${NAV4_BUSINESS_NAV_HEIGHT} - ${
	BUSINESS_WRAPPER_PADDING * 2
}px)`;

export const BUSINESS_WRAPPER_WIDTH = `calc(100vw - ${UNSAFE_DO_NOT_USE_LEFT_PANEL_WIDTH} - ${UNSAFE_DO_NOT_USE_LEFT_SIDEBAR_WIDTH} - ${UNSAFE_DO_NOT_USE_RIGHT_PANEL_WIDTH} - ${UNSAFE_DO_NOT_USE_RIGHT_SIDEBAR_WIDTH}) !important`;
export const BUSINESS_WRAPPER_WITH_BACKGROUND_WIDTH = `calc(100vw - ${UNSAFE_DO_NOT_USE_LEFT_PANEL_WIDTH} - ${UNSAFE_DO_NOT_USE_LEFT_SIDEBAR_WIDTH} - ${UNSAFE_DO_NOT_USE_RIGHT_PANEL_WIDTH} - ${UNSAFE_DO_NOT_USE_RIGHT_SIDEBAR_WIDTH} - ${
	BUSINESS_WRAPPER_PADDING * 2
}px) !important`;

// Maybe won't need this
export const ELEVATION_SURFACE_CONTRAST_OPACITY = 82;
